import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import useOnClickOutside from "use-onclickoutside";
import { icon } from "../../../../../../../../config/constants";
import "../../../../../styles/ExpressionBuilder.css";

/**
 * Options popup component for ComboBox items with sub-options
 * @param {boolean} showPopup - Whether to show the popup
 * @param {Object} popupAnchor - Anchor element for popup positioning
 * @param {Object} hoveredItem - Currently hovered item with options
 * @param {Function} onOptionClick - Handler for option selection
 * @param {Function} onClose - Handler for popup close
 * @returns {JSX.Element} Options popup component
 * @author Abhishek Dhillon
 * @parent ComboBoxInput
 */
const OptionsPopup = ({ showPopup, popupAnchor, hoveredItem, onOptionClick, onClose, setComboObj }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const popupRef = useRef(null);
  useOnClickOutside(popupRef, onClose);
  /**
   * Filter options based on search term
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (!hoveredItem?.options) {
      setFilteredOptions([]);
      return;
    }
    setComboObj((prev) => ({ ...prev, open: false }));
    const filtered = hoveredItem.options.filter((option) => {
      const label = option?.label || option?.value || option;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    setFilteredOptions(filtered.sort((a, b) => a.label.localeCompare(b.label)));
  }, [hoveredItem?.options, searchTerm]);

  /**
   * Reset search when popup opens/closes
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (showPopup) {
      setSearchTerm("");
    }
  }, [showPopup]);

  /**
   * Handles search input change
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  /**
   * Handles clicking on an option
   * @param {string} optionValue - Selected option value
   * @author Abhishek Dhillon
   */
  const handleOptionClick = useCallback(
    (e, optionValue, itemHovered) => {
      e.preventDefault();
      if (onOptionClick) {
        onOptionClick(e, {
          name: optionValue?.label || optionValue?.value || optionValue,
          value: optionValue?.value || optionValue?.label || optionValue,
          custom: false,
          group: itemHovered?.group,
          ref: {
            id: itemHovered?.id,
            name: itemHovered?.name,
            chipType: itemHovered?.chipType,
            adminType: itemHovered?.adminType,
          },
        });
      }
    },
    [onOptionClick]
  );

  /**
   * Handles closing the popup
   * @author Abhishek Dhillon
   */
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const anchorElement = popupAnchor?.current;
  const hasValidAnchor = anchorElement && typeof anchorElement === "object" && anchorElement instanceof Element;
  if (!showPopup || !hoveredItem?.options?.length || !hasValidAnchor) {
    return null;
  }

  return (
    <Popup
      anchor={anchorElement}
      show={showPopup}
      popupClass='combobox-options-popup'
      onClose={handleClose}
      collision={{ horizontal: "flip", vertical: "flip" }}>
      <div className='popup-content' ref={popupRef}>
        <div className='popup-header'>
          <strong>Options:</strong>
          <Button
            fillMode='flat'
            icon='cvlose'
            size='small'
            onClick={handleClose}
            className='popup-close-btn'
            id={`close-popup-${hoveredItem?.id}`}
          />
        </div>

        {/* Search Input */}
        <div className='popup-search'>
          <Input
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder='Search options...'
            className='search-input'
            id={`search-${hoveredItem?.id}`}
            size='small'
            autoFocus={true}
          />
        </div>

        {/* Options List */}
        <div className='popup-options'>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <Button
                key={index}
                fillMode='flat'
                className='justify-content-start'
                onClick={(e) => handleOptionClick(e, option, hoveredItem)}
                id={`option-${hoveredItem?.id}-${index}`}>
                <span className='d-flex align-items-center gap-2 cursor-pointer'>
                  {option.icon && <span>{icon[option.icon]}</span>}
                  {option?.label || option?.value || option}
                </span>
              </Button>
            ))
          ) : (
            <div className='no-options-message'>{searchTerm ? `No options found for "${searchTerm}"` : "No options available"}</div>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default OptionsPopup;
